import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trophy, ClipboardCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COUNTRIES, CURRENCIES, fmtMoney } from "@/lib/data";
import { useAuth } from "@/lib/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BID_STATUS = {
  submitted: "bg-slate-100 text-slate-600",
  evaluated: "bg-amber-50 text-amber-700",
  awarded: "bg-emerald-50 text-emerald-700",
  rejected: "bg-red-50 text-red-500",
};

export default function BidPanel({ tender, role, onChange }) {
  const { user } = useAuth();
  const [bids, setBids] = useState([]);
  const [form, setForm] = useState({ vendor_name: user.organization || user.name || "", vendor_country: "", vendor_email: user.email || "", bid_amount: "", currency: tender.currency, technical_proposal: "", delivery_timeline: "" });
  const [evalTarget, setEvalTarget] = useState(null);
  const [evalForm, setEvalForm] = useState({ technical_score: "", financial_score: "", evaluation_notes: "" });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(() => {
    axios.get(`${API}/tenders/${tender.id}/bids`).then((r) => setBids(r.data));
  }, [tender.id]);
  useEffect(() => { load(); }, [load]);

  const submitBid = async (e) => {
    e.preventDefault();
    if (!form.vendor_name || !form.vendor_country || !form.bid_amount || !form.technical_proposal) {
      toast.error("Please complete all required bid fields.");
      return;
    }
    try {
      await axios.post(`${API}/tenders/${tender.id}/bids`, { ...form, bid_amount: parseFloat(form.bid_amount) });
      toast.success("Bid submitted successfully.");
      setForm({ vendor_name: user.organization || user.name || "", vendor_country: "", vendor_email: user.email || "", bid_amount: "", currency: tender.currency, technical_proposal: "", delivery_timeline: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bid submission failed.");
    }
  };

  const submitEvaluation = async () => {
    try {
      await axios.post(`${API}/bids/${evalTarget.id}/evaluate`, {
        technical_score: parseFloat(evalForm.technical_score),
        financial_score: parseFloat(evalForm.financial_score),
        evaluation_notes: evalForm.evaluation_notes,
      });
      toast.success("Bid evaluated.");
      setEvalTarget(null);
      setEvalForm({ technical_score: "", financial_score: "", evaluation_notes: "" });
      load();
      onChange();
    } catch {
      toast.error("Evaluation failed.");
    }
  };

  const award = async (bidId) => {
    try {
      await axios.post(`${API}/tenders/${tender.id}/award`, { bid_id: bidId });
      toast.success("Contract awarded.");
      load();
      onChange();
    } catch {
      toast.error("Award failed.");
    }
  };

  const canManage = role !== "vendor";
  const canBid = role === "vendor" && tender.status === "published";
  const inputCls = "bg-white border-slate-300";

  return (
    <div className="grid lg:grid-cols-3 gap-8 items-start">
      <div className="lg:col-span-2 bg-white border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <h3 className="font-serif text-lg text-slate-900">Bids Received</h3>
          <span className="font-mono text-sm text-slate-500" data-testid="bid-count">{bids.length} bid{bids.length !== 1 ? "s" : ""}</span>
        </div>
        {bids.length === 0 ? (
          <p className="text-sm text-slate-400 p-10 text-center" data-testid="bids-empty-state">
            {tender.status === "draft" ? "Publish the tender to start receiving bids." : "No bids submitted yet."}
          </p>
        ) : (
          <div className="divide-y divide-slate-100">
            {bids.map((b, i) => (
              <div key={b.id} className="p-6" data-testid={`bid-row-${i}`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-medium text-slate-900">{b.vendor_name}</span>
                      <span className="text-xs text-slate-400">{b.vendor_country}</span>
                      <span className={`text-[11px] px-2 py-0.5 ${BID_STATUS[b.status]}`}>{b.status}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1.5 line-clamp-2 max-w-xl">{b.technical_proposal}</p>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-right">
                      <p className="font-mono font-semibold text-slate-900">{fmtMoney(b.bid_amount, b.currency)}</p>
                      {b.total_score != null && <p className="font-mono text-xs text-[#0050A0]">Score: {b.total_score}/100</p>}
                    </div>
                    {canManage && b.status === "submitted" && tender.status !== "awarded" && (
                      <button onClick={() => setEvalTarget(b)} data-testid={`evaluate-bid-btn-${i}`} className="inline-flex items-center gap-1.5 border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-900 hover:text-white transition-colors duration-200">
                        <ClipboardCheck className="w-3.5 h-3.5" aria-hidden="true" /> Evaluate
                      </button>
                    )}
                    {canManage && b.status === "evaluated" && tender.status !== "awarded" && (
                      <button onClick={() => award(b.id)} data-testid={`award-bid-btn-${i}`} className="inline-flex items-center gap-1.5 bg-emerald-600 text-white px-3 py-2 text-xs font-semibold hover:bg-emerald-700 transition-colors duration-200">
                        <Trophy className="w-3.5 h-3.5" aria-hidden="true" /> Award
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 p-6">
        <h3 className="font-serif text-lg text-slate-900 mb-1">Submit a Bid</h3>
        {!canBid ? (
          <p className="text-xs text-slate-400 mt-3 leading-relaxed" data-testid="bid-form-disabled-note">
            {role !== "vendor"
              ? "Only registered vendor accounts can submit bids."
              : tender.status === "draft"
              ? "This tender is not yet published."
              : "Bidding is closed for this tender."}
          </p>
        ) : (
          <form onSubmit={submitBid} className="space-y-4 mt-4">
            <Input data-testid="bid-vendor-name" className={inputCls} placeholder="Company name *" value={form.vendor_name} onChange={(e) => set("vendor_name")(e.target.value)} />
            <Select value={form.vendor_country} onValueChange={set("vendor_country")}>
              <SelectTrigger data-testid="bid-vendor-country" className={inputCls}><SelectValue placeholder="Country *" /></SelectTrigger>
              <SelectContent className="max-h-64">{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="bid-vendor-email" type="email" className={inputCls} placeholder="Contact email" value={form.vendor_email} onChange={(e) => set("vendor_email")(e.target.value)} />
            <div className="flex gap-3">
              <Input data-testid="bid-amount" type="number" min="0" className={`${inputCls} font-mono flex-1`} placeholder="Bid amount *" value={form.bid_amount} onChange={(e) => set("bid_amount")(e.target.value)} />
              <Select value={form.currency} onValueChange={set("currency")}>
                <SelectTrigger data-testid="bid-currency" className={`${inputCls} w-24`}><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Textarea data-testid="bid-technical-proposal" className={`${inputCls} min-h-[90px]`} placeholder="Technical proposal summary *" value={form.technical_proposal} onChange={(e) => set("technical_proposal")(e.target.value)} />
            <Input data-testid="bid-delivery-timeline" className={inputCls} placeholder="Delivery timeline (e.g., 90 days)" value={form.delivery_timeline} onChange={(e) => set("delivery_timeline")(e.target.value)} />
            <button type="submit" data-testid="submit-bid-btn" className="w-full bg-[#0050A0] text-white py-3 text-sm font-semibold hover:bg-[#003F7A] transition-colors duration-200">
              Submit Bid
            </button>
          </form>
        )}
      </div>

      <Dialog open={!!evalTarget} onOpenChange={(o) => !o && setEvalTarget(null)}>
        <DialogContent className="rounded-none" data-testid="evaluation-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif">Evaluate Bid — {evalTarget?.vendor_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Technical Score (0–100)</Label>
                <Input data-testid="eval-technical-score" type="number" min="0" max="100" className="font-mono" value={evalForm.technical_score} onChange={(e) => setEvalForm((f) => ({ ...f, technical_score: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Financial Score (0–100)</Label>
                <Input data-testid="eval-financial-score" type="number" min="0" max="100" className="font-mono" value={evalForm.financial_score} onChange={(e) => setEvalForm((f) => ({ ...f, financial_score: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Evaluation Notes</Label>
              <Textarea data-testid="eval-notes" value={evalForm.evaluation_notes} onChange={(e) => setEvalForm((f) => ({ ...f, evaluation_notes: e.target.value }))} />
            </div>
            <p className="text-xs text-slate-400">Weighted total = Technical × 0.7 + Financial × 0.3</p>
            <button onClick={submitEvaluation} data-testid="submit-evaluation-btn" className="w-full bg-[#0050A0] text-white py-3 text-sm font-semibold hover:bg-[#003F7A] transition-colors duration-200">
              Record Evaluation
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
